import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { cn } from "../../../lib/utils";

interface StepProps {
  title: string;
  children: React.ReactNode;
}

interface StepContextType {
  stepNumber: number;
}

const StepContext = React.createContext<StepContextType>({ stepNumber: 0 });

export function Step({ title, children }: StepProps) {
  const { stepNumber } = React.useContext(StepContext);

  return (
    <div className="flex gap-4 mb-6 last:mb-0">
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-8 h-8 rounded-none border-2 border-[var(--su-purple-accent)] bg-[var(--su-bg-secondary)] flex items-center justify-center text-[var(--su-purple-accent)] font-bold text-sm">
          {stepNumber}
        </div>
        <div className="w-0.5 flex-1 bg-[var(--su-border-primary)] mt-2" />
      </div>
      <div className="flex-1 pt-1">
        <h3 className="text-lg font-semibold text-[var(--su-text-primary)] mb-2">
          {title}
        </h3>
        <div className="text-[var(--su-text-secondary)] leading-relaxed">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              text: ({ children }) => {
                // Render text nodes as-is (not as code)
                return <>{children}</>;
              },
              p: ({ children }) => {
                if (!children) return null;
                return (
                  <p className="m-0 mb-2 text-[var(--su-text-secondary)] leading-relaxed">
                    {children}
                  </p>
                );
              },
              a: ({ href, children }) => {
                const isExternal =
                  href?.startsWith("http") || href?.startsWith("mailto:");
                return (
                  <a
                    href={href}
                    className="text-[var(--su-purple-accent)] hover:underline"
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                  >
                    {children}
                  </a>
                );
              },
              strong: ({ children }) => (
                <strong className="font-semibold text-[var(--su-text-primary)]">
                  {children}
                </strong>
              ),
              em: ({ children }) => (
                <em className="italic text-[var(--su-text-secondary)]">
                  {children}
                </em>
              ),
              code: ({ children, className: codeClassName }) => {
                const isInline = !codeClassName;
                return isInline ? (
                  <code className="px-1.5 py-0.5 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] text-[var(--su-purple-accent)] text-sm rounded-none font-mono">
                    {children}
                  </code>
                ) : (
                  <code className={codeClassName}>{children}</code>
                );
              },
              pre: ({ children }) => {
                // Don't render code blocks - this shouldn't happen in step content
                return <>{children}</>;
              },
              ul: ({ children }) => (
                <ul className="mt-2 mb-2 ml-4 list-disc">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mt-2 mb-2 ml-4 list-decimal">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="text-[var(--su-text-secondary)]">{children}</li>
              ),
            }}
          >
            {typeof children === "string"
              ? children.trim()
              : String(children).trim()}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

interface DocsStepByStepProps {
  children: React.ReactNode;
  className?: string;
}

export function DocsStepByStep({ children, className }: DocsStepByStepProps) {
  const steps = React.Children.toArray(children);

  return (
    <div className={cn("my-8", className)}>
      {React.Children.map(steps, (child, index) => {
        if (React.isValidElement<StepProps>(child)) {
          return (
            <StepContext.Provider key={index} value={{ stepNumber: index + 1 }}>
              {child}
            </StepContext.Provider>
          );
        }
        return child;
      })}
    </div>
  );
}
